import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { subscribeToTelemetry } from '@/lib/realtime';

import { mapDeviceKeys } from './map-hooks';
import { getDeviceMapPopup } from './map-popup-api';
import { applyTelemetry } from './map-popup-rules';
import { telemetryUpdateSchema, type DeviceMapPopup } from './map-popup-schemas';

/** Namespaced under the marker layer's own factory, so opening a panel and reloading the layer stay
 *  independent caches under one root. */
export const mapPopupKeys = {
  popup: (deviceId: string) => [...mapDeviceKeys.map(), 'popup', deviceId] as const,
};

/**
 * One Device's quick-view payload, fetched **while its panel is open and only then**.
 *
 * Called from `<DeviceQuickView>` rather than from the page, deliberately: the query's lifetime is
 * then the panel's by construction, which is what ticket 12's live refresh will hang off. `null`
 * disables it, so no request is made for a Device nobody selected.
 *
 * App defaults otherwise — this is not the load-time snapshot the marker layer is (ADR-0016), it is a
 * read of one Device the Grower just asked about.
 */
export function useDeviceMapPopup(deviceId: string | null) {
  return useQuery({
    queryKey: mapPopupKeys.popup(deviceId ?? ''),
    queryFn: () => getDeviceMapPopup(deviceId as string),
    enabled: deviceId !== null,
  });
}

/**
 * Subscribe the open quick view to the typed `telemetry` channel and mirror what arrives into
 * `useDeviceMapPopup`'s cache entry (ADR-0015; grilling Q6).
 *
 * **Mirroring, rather than a second piece of state, is the whole point.** The panel keeps reading
 * one query, so a live reading and a fetched one cannot disagree and nothing re-mounts to show a
 * new value — which is how §9a's "refresh in place", §9b's "does not flicker, reposition or resize"
 * and §5e's "never force-closed" hold by construction rather than by care.
 *
 * **Three refusals.** An event for another Device is dropped (§9g: two tabs, two panels, no
 * crossover). An unparseable event is dropped rather than written (§9d: an unknown contract freezes
 * the values, it does not blank them). And an event arriving before the initial fetch resolves is
 * dropped, because there is nothing yet to merge into.
 *
 * **Suspended while the tab is hidden** (§9f, resolved in grilling Q6): the subscription is torn
 * down on `visibilitychange` and re-established on return, so an open tab in the background holds
 * no connection. Catching up on return is **not** built here — `<QueryProvider>` already sets
 * `refetchOnWindowFocus: true`, so returning to the tab refetches `map-popup` and the values reflect
 * the current state rather than the moment the tab was left. `refetchOnReconnect: true` does the
 * same for §15c.
 *
 * Nothing in this hook reports a connection state to anyone, which is §9d/§9e: reconnection is
 * silent, and neither the interruption nor the recovery is announced.
 */
export function useLiveTelemetry(deviceId: string | null): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (deviceId === null) return;

    let unsubscribe: (() => void) | null = null;

    const mirror = (payload: unknown) => {
      const parsed = telemetryUpdateSchema.safeParse(payload);
      if (!parsed.success || parsed.data.id !== deviceId) return;

      queryClient.setQueryData<DeviceMapPopup>(mapPopupKeys.popup(deviceId), (current) =>
        current ? applyTelemetry(current, parsed.data) : current,
      );
    };

    const sync = () => {
      const visible = document.visibilityState === 'visible';
      if (visible && unsubscribe === null) {
        unsubscribe = subscribeToTelemetry(mirror);
      } else if (!visible && unsubscribe !== null) {
        unsubscribe();
        unsubscribe = null;
      }
    };

    sync();
    document.addEventListener('visibilitychange', sync);

    return () => {
      document.removeEventListener('visibilitychange', sync);
      unsubscribe?.();
    };
  }, [deviceId, queryClient]);
}
